import { Controller } from './game/controller';
import { GAME_STATUSES, ORIENTATIONS } from './game/constants';
import { ee } from './eventEmitter';
import { analyticsEvent } from './analytics';
import { Auth } from './app/auth';
import { Rating } from './app/rating';
import { MouseMovement } from './animate/mouseMovement';
import { getResultImageGenerator } from './app/resultImageGenerator';

const APP_STATUSES = {
    startGreeting: 'StartGreeting',
    startRules: 'startRules',
    startControl: 'startControl',
    gameOrientation: 'gameOrientation',
    gamePlay: 'gamePlay',
    gamePause: 'gamePause',
    menuStart: 'menuStart',
    soundMenu: 'soundMenu',
    controlMenu: 'controlMenu',
    rulesMenu: 'rulesMenu',
    results: 'results'
};

export class Base {
    mainContainer;
    pausePage;
    resultsPage;
    orientationErrorPage;
    userOffMusic = false;
    userOffEffects = false;
    #appStatus = APP_STATUSES.startGreeting;

    // use buttons
    #pauseGameButton;
    #toStartRulesButton;
    #toStartControlButton;
    #startGameButton;
    #closeMenu;
    #restartGameButton;

    //
    #jsImagesStoreArr;

    constructor(jsImagesStoreArr) {
        this.#jsImagesStoreArr = jsImagesStoreArr;
        this.controller = new Controller();
        this.auth = new Auth();
        this.rating = new Rating(this.auth);
        this.mouseMovement = new MouseMovement();
        this.mouseMovement.startMouseTracking();
        this.initEvents();
        this.initListeners();
    }

    initEvents() {
        this.initButtonsEvents();
        this.initKeyDownEvents();

        window.addEventListener('resize', () => {
            this.controller.windowResize();
            this.mouseMovement.windowResize();
            this.rating.calcRatingPageHeight();
        });
    }

    initListeners() {
        // ee.on('loadDone', this.loaded.bind(this));
        ee.on('changeOrientation', this.changeOrientation.bind(this));
        ee.on(GAME_STATUSES.BEFORE_PLAY, () => {
            this.#appStatus = APP_STATUSES.gamePlay;
            this.togglePausePage('remove', null);
        });
        ee.on(GAME_STATUSES.END, this.endGame.bind(this));
        ee.on('takeEgg', () => {
            if (!this.userOffEffects) this.playTakenEggSound();
        });
        ee.on('fallenEgg', () => {
            if (!this.userOffEffects) this.playFallenEggSound();
        });

        ee.on('toggleInsidePage', () => {
            // eslint-disable-next-line
            switch (this.#appStatus) {
                case 'StartGreeting':
                case 'startRules':
                case 'startControl':
                    this.toggleAllStartPage();

                    break;
                case 'results':
                    this.toggleResults();
                    this.toggleLayout('main');

                    break;

                case APP_STATUSES.menuStart:
                    this.toggleMenuButtons();

                    break;
                case APP_STATUSES.soundMenu:
                    this.toggleInsideMenu('sound');

                    break;
                case APP_STATUSES.controlMenu:
                    this.toggleInsideMenu('control');

                    break;
                case APP_STATUSES.rulesMenu:
                    this.toggleInsideMenu('rules');

            }
        });
    }

    startGame() {
        this.toggleGamePage();
        this.controller.startGame();
        this.mouseMovement.stopMouseTracking();
        this.#appStatus = APP_STATUSES.gamePlay;
    }

    initButtonsEvents() {
        // link
        const links = document.getElementsByClassName('logo');

        Array.from(links).forEach((link) => {
            link.addEventListener('click', () => {
                analyticsEvent('link', 'click', 'logo');
            });
        });

        this.#toStartRulesButton = document.getElementById('toStartRules');
        this.#toStartControlButton = document.getElementById('toStartControl');
        this.#startGameButton = document.getElementById('startGame');

        this.#pauseGameButton = document.getElementById('pauseGame');
        const continueGameButton = document.getElementById('continueGame');

        this.#restartGameButton = document.getElementById('restartGame');
        const saveResultsButton = document.getElementById('saveResults');
        // music
        const toggleAllMusicButton = document.getElementById('toggleAllMusic');
        const toggleEffectsButton = document.getElementById('effectsButton');
        const toggleMusicButton = document.getElementById('musicButton');

        this.#toStartRulesButton.addEventListener('click', () => {
            analyticsEvent('start page', 'click', 'go to rules page');

            const startPage = document.getElementById('startPage');

            startPage.classList.remove('start-page_flex-end');

            this.toggleStartPage('greeting');
            this.toggleStartPage('rules');
            this.#appStatus = APP_STATUSES.startRules;
        });

        this.#toStartControlButton.addEventListener('click', () => {
            analyticsEvent('start page', 'click', 'go to control page');

            this.toggleStartPage('rules');
            this.toggleStartPage('control');
            this.#appStatus = APP_STATUSES.startControl;
        });

        this.#startGameButton.addEventListener('click', () => {
            analyticsEvent('start page', 'click', 'start game');

            this.toggleStartPage('control');
            this.toggleAllStartPage();
            this.toggleLayout('main');
            this.startGame();
            if (!this.userOffMusic) this.toggleAudio();
        });

        this.#pauseGameButton.addEventListener('click', () => {
            analyticsEvent('game', 'click', 'pause');

            this.togglePause();
        });

        continueGameButton.addEventListener('click', () => {
            analyticsEvent('game', 'click', 'continue');

            this.togglePause();
        });

        this.#restartGameButton.addEventListener('click', () => {
            analyticsEvent('results page', 'click', 'restart');

            if (!this.userOffMusic) this.restartAllAudio();
            this.toggleResults();
            this.toggleGamePage();
            this.controller.restartGame();
            this.#appStatus = APP_STATUSES.gamePlay;
        });

        toggleAllMusicButton.addEventListener('click', () => {
            if (this.userOffMusic && this.userOffEffects) {
                analyticsEvent('game', 'click', 'on all sounds');
            } else {
                analyticsEvent('game', 'click', 'off all sounds');
            }

            this.userOffMusic = !this.userOffMusic;
            this.userOffEffects = !this.userOffEffects;
            this.toggleMusicButton('musicButton');
            this.toggleMusicButton('effectsButton');
            this.toggleMusicButton('toggleAllMusic', true);
            this.toggleAudio();
        });

        toggleEffectsButton.addEventListener('click', () => {
            if (this.userOffEffects) {
                analyticsEvent('game menu', 'click', 'off effect sounds');
            } else {
                analyticsEvent('game menu', 'click', 'on effect sounds');
            }

            this.userOffEffects = !this.userOffEffects;
            this.toggleMusicButton('effectsButton');
            this.toggleMusicButton('toggleAllMusic', true);
        });

        toggleMusicButton.addEventListener('click', () => {
            if (this.userOffMusic) {
                analyticsEvent('game menu', 'click', 'off music');
            } else {
                analyticsEvent('game menu', 'click', 'on music');
            }

            this.userOffMusic = !this.userOffMusic;
            this.toggleMusicButton('musicButton');
            this.toggleMusicButton('toggleAllMusic', true);
            this.toggleAudio();
        });

        saveResultsButton.addEventListener('click', () => {
            this.rating.saveResult(this.controller.score);
            this.auth.toggleAuthPage(true);
        });

        this.initMenuButtons();
    }

    initMenuButtons() {
        this.#closeMenu = document.querySelector('.close-menu');
        const controlButtons = document.querySelectorAll('.control-menu-button');
        const menuButton = document.getElementById('menu');

        menuButton.addEventListener('click', () => {
            analyticsEvent('game', 'click', 'open menu');
            this.mouseMovement.startMouseTracking();

            this.controller.pause();
            this.toggleLayout('main');
            this.toggleGamePage();
            this.toggleMenu();
            this.#appStatus = APP_STATUSES.menuStart;
        });

        this.#closeMenu.addEventListener('click', () => {
            analyticsEvent('game menu', 'click', 'close menu');
            this.mouseMovement.stopMouseTracking();

            this.toggleGamePage();
            this.toggleMenu();
            this.toggleLayout('main');
            this.controller.continueGame();
            this.#appStatus = APP_STATUSES.gamePlay;
        });

        Array.from(controlButtons).forEach((val) => {
            val.addEventListener('click', () => {
                analyticsEvent('game menu', 'click', `toggle ${val.getAttribute('control-page')}`);

                const controlPage = val.getAttribute('control-page');

                console.log(controlPage);

                this.toggleInsideMenu(controlPage);
            });
        });
    }

    initKeyDownEvents() {
        document.addEventListener('keydown', (event) => {
            if (event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) return;

            // eslint-disable-next-line
            switch (this.#appStatus) {
                case APP_STATUSES.gamePlay:
                    if (event.code === 'Space') {
                        this.throwEvent(this.#pauseGameButton);
                    } else {
                        this.controller.pressedControlKey(event.code);
                    }

                    break;
                case APP_STATUSES.gamePause:
                    if (event.code === 'Space') this.throwEvent(this.#pauseGameButton);

                    break;
                case APP_STATUSES.startGreeting:
                    if (event.code === 'Enter') this.throwEvent(this.#toStartRulesButton);

                    break;
                case APP_STATUSES.startRules:
                    if (event.code === 'Enter') this.throwEvent(this.#toStartControlButton);

                    break;
                case APP_STATUSES.startControl:
                    if (event.code === 'Enter') this.throwEvent(this.#startGameButton);

                    break;
                case APP_STATUSES.menuStart:
                    if (event.code === 'Escape') this.throwEvent(this.#closeMenu);

                    break;
                case APP_STATUSES.soundMenu:
                    if (event.code === 'Escape') this.toggleInsideMenu('sound');

                    break;
                case APP_STATUSES.controlMenu:
                    if (event.code === 'Escape') this.toggleInsideMenu('control');

                    break;
                case APP_STATUSES.rulesMenu:
                    if (event.code === 'Escape') this.toggleInsideMenu('rules');

                    break;
                case APP_STATUSES.results:
                    if (event.code === 'Enter') this.throwEvent(this.#restartGameButton);

                    break;
            }
        });
    }

    // eslint-disable-next-line
    throwEvent(el) {
        if (document.activeElement !== el) {
            el.click();
        }
    }

    // bt button events
    togglePause() {
        if (this.#appStatus === APP_STATUSES.gamePause) {
            this.play();
        } else if (this.#appStatus === APP_STATUSES.gamePlay) {
            this.pause();
        }
    }

    pause() {
        this.controller.pause();
        this.togglePausePage();
        this.#appStatus = APP_STATUSES.gamePause;
    }

    play() {
        this.controller.continueGame();
        this.#appStatus = APP_STATUSES.gamePlay;
    }

    endGame() {
        if (!this.userOffMusic) this.toggleAudio();
        this.toggleGamePage();
        this.toggleResults();
        this.mouseMovement.startMouseTracking();

        this.resultImageGenerator = getResultImageGenerator(this.#jsImagesStoreArr);
        this.resultImageGenerator.value = this.controller.score;

        this.#appStatus = APP_STATUSES.results;
    }

    // eslint-disable-next-line
    toggleAllStartPage() {
        const startPage = document.getElementById('startPage');

        startPage.classList.toggle('start-page_active');
    }

    // eslint-disable-next-line
    toggleGamePage() {
        const gamePageBaseClass = 'game';
        const gamePage = document.querySelector(`.${gamePageBaseClass}`);

        gamePage.classList.toggle(`${gamePageBaseClass}_active`);
    }

    // eslint-disable-next-line
    toggleLayout(type = 'main') {
        const mainLayout = document.getElementById(`${type}Layout`);

        mainLayout.classList.toggle(`${type}-layout_active`);
    }
    // eslint-disable-next-line
    toggleStartPage(startPageName) {
        const startPage = document.getElementById(`${startPageName}StartPage`);

        startPage.classList.toggle(`${startPageName}-start-page_active`);
    }

    // eslint-disable-next-line
    toggleMenu() {
        const menuPage = document.getElementById('menuPage');

        menuPage.classList.toggle('menu-page_active');

        setTimeout(() => {
            this.toggleMenuButtons();
        }, 0);
    }

    // eslint-disable-next-line
    toggleMenuButtons() {
        const menuButtons = document.getElementById('menuButtons');

        menuButtons.classList.toggle('menu-buttons_active');
    }

    // eslint-disable-next-line
    toggleInsideMenu(menuName) {
        const menu = document.getElementById(`menu${menuName}`);

        menu.classList.toggle(`menu-${menuName}_active`);

        this.toggleMenuButtons();
        this.#appStatus = this.#appStatus === APP_STATUSES.menuStart ? APP_STATUSES[`${menuName}Menu`] : APP_STATUSES.menuStart;
    }

    // eslint-disable-next-line
    togglePausePage(action = 'toggle') {
        const pausePage = document.getElementById('pausePage');

        pausePage.classList[action]('pause-page_active');
    }

    // eslint-disable-next-line
    toggleResults() {
        const resultsPage = document.getElementById('resultsPage');

        resultsPage.classList.toggle('results-page_active');
    }

    // eslint-disable-next-line
    toggleOrientationPage(action) {
        const orientationPage = document.getElementById('orientationPage');

        orientationPage.classList[action]('orientation-page_active');
    }

    // eslint-disable-next-line
    toggleAudio() {
        const audio = document.getElementById('audio');

        if (audio.paused) {
            audio.play();
        } else audio.pause();
    }

    // eslint-disable-next-line
    playTakenEggSound() {
        const audio = document.getElementById('takenEggAudio');

        audio.play();
    }

    // eslint-disable-next-line
    playFallenEggSound() {
        const audio = document.getElementById('fallenEggAudio');

        audio.play();
    }

    // eslint-disable-next-line
    toggleMusicButton(elementId, isAllMisic) {
        const button = document.getElementById(elementId);

        if (isAllMisic) {
            if (this.userOffEffects === this.userOffMusic) button.classList.toggle('audio-control_off');
        } else {
            button.classList.toggle('audio-control_off');
        }
    }

    restartAllAudio() {
        const audio = document.getElementById('audio');

        audio.currentTime = 0;
        this.toggleAudio();
    }

    changeOrientation(orientation) {
        if (orientation === ORIENTATIONS.PORTRAIT) {
            this.toggleOrientationPage('add');
            this.#appStatus = APP_STATUSES.gameOrientation;
        } else {
            this.toggleOrientationPage('remove');
            this.#appStatus = APP_STATUSES.gamePlay;
        }
    }
}
